import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { config } from '../../config';
import { closeExpandableColumn } from '../../redux/Slices/expandableColumnSlice';
import ImagePlaceholder from '../Global/ImagePlaceholder';
import Spinner from '../Global/Spinner';
import './NotificationsColumn.scss';

const NotificationsColumn = () => {
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showUnreadOnly, setShowUnreadOnly] = useState(false);
    const [lastFetchTime, setLastFetchTime] = useState(0);
    const dispatch = useDispatch();

    useEffect(() => {
        // Only fetch if we haven't fetched recently (within 30 seconds)
        const now = Date.now();
        if (now - lastFetchTime > 30000) {
            fetchNotifications();
        } else {
            setLoading(false);
        }
    }, [lastFetchTime]);

    const fetchNotifications = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${config.API_URI}/v1/notifications`, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) throw new Error('Failed to fetch notifications');

            const data = await response.json();
            setNotifications(data.notifications || []);
            setLastFetchTime(Date.now()); // Update last fetch time
        } catch (error) {
            console.error('Error fetching notifications:', error);
        } finally {
            setLoading(false);
        }
    };

    const markAsRead = async (notificationId) => {
        try {
            await fetch(`${config.API_URI}/v1/notifications/${notificationId}`, {
                method: 'PUT',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            setNotifications(prev =>
                prev.map(n => n._id === notificationId ? { ...n, read: true } : n)
            );
        } catch (error) {
            console.error('Error marking notification as read:', error);
        }
    };

    const handleMarkAllAsRead = async () => {
        try {
            await fetch(`${config.API_URI}/v1/notifications/mark-all-read`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
            });
            setNotifications(prev => prev.map(n => ({ ...n, read: true })));
        } catch (error) {
            console.error('Error marking all notifications as read:', error);
        }
    };

    const handleNotificationClick = (notification) => {
        markAsRead(notification._id);
        dispatch(closeExpandableColumn());
    };

    // Filter notifications based on unread toggle
    const filteredNotifications = showUnreadOnly
        ? notifications.filter(n => !n.read)
        : notifications;

    return (
        <div className='notifications-column'>
            <div className="notifications-column__content">
                <div className="notifications-header">
                    <div className="header-actions">
                        <button
                            onClick={handleMarkAllAsRead}
                            className="mark-all-read-btn"
                            disabled={notifications.every(n => n.read)}
                        >
                            Mark all as read
                        </button>
                    </div>

                    <div className="filter-controls">
                        <label className="unread-filter">
                            <span className="filter-label">Unreads</span>
                            <input
                                type="checkbox"
                                checked={showUnreadOnly}
                                onChange={(e) => setShowUnreadOnly(e.target.checked)}
                            />
                        </label>
                    </div>
                </div>

                {loading ? (
                    <div className="loading-container">
                        <Spinner size={30} color="#3498db" speed={1.5} />
                    </div>
                ) : filteredNotifications.length > 0 ? (
                    <div className="notifications-list">
                        {filteredNotifications.slice(0, 20).map(notification => {
                            // Format notification message based on type
                            let message = notification.message;
                            if (notification.type === 'card_comment') {
                                message = 'commented on a card';
                            } else if (notification.type === 'card_assignment') {
                                message = 'assigned you to a card';
                            } else if (notification.type === 'board_invitation') {
                                message = 'invited you to a board';
                            }

                            console.log({ notification });

                            // Skip notifications without initiator data
                            if (!notification.initiator) {
                                return null;
                            }

                            return (
                                <div
                                    key={notification._id}
                                    className={`notification-item ${!notification.read ? 'unread' : ''}`}
                                >
                                    <div className="notification-avatar">
                                        {notification.initiator.profilePicture ? (
                                            <img
                                                src={notification.initiator.profilePicture}
                                                alt={notification.initiator.name}
                                                className="avatar-image"
                                                loading="lazy"
                                            />
                                        ) : (
                                            <ImagePlaceholder
                                                size={35}
                                                member={notification.initiator}
                                                text={notification.initiator.username}
                                            />
                                        )}
                                    </div>

                                    <div className="notification-content">
                                        <div className="notification-text">
                                            <strong>{notification.initiator.username}</strong> {message}
                                        </div>
                                        <div className="notification-date">
                                            {new Date(notification.createdAt).toLocaleString()}
                                        </div>
                                    </div>

                                    {!notification.read && <div className="unread-indicator" />}

                                    {notification.targetType && (
                                        <Link
                                            className="notification-link"
                                            onClick={() => handleNotificationClick(notification)}
                                            to={`/c/${notification.targetId}`}
                                        />
                                    )}
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="empty-state">
                        <div className="empty-icon">
                            🔔
                        </div>
                        <h3>{showUnreadOnly ? 'No unread notifications' : 'No notifications'}</h3>
                        <p>{showUnreadOnly ? 'All notifications have been read!' : 'You\'re all caught up! New notifications will appear here.'}</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default NotificationsColumn;
